'use client';

import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react';

import { useAuth } from '@/providers/AuthProvider';
import { ChatType, WriteListingInfo } from '@/types/chat';

interface ChatContextProps {
  channels: ChatType[];
  setChannels: React.Dispatch<React.SetStateAction<ChatType[]>>;
  writeListingInfo: WriteListingInfo | null;
  setWriteListingInfo: (info: WriteListingInfo | null) => void;
  removeWriteListingInfo: () => void;
  chatError: boolean;
  setChatError: React.Dispatch<React.SetStateAction<boolean>>;
  selectedChannel: ChatType | null;
  setSelectedChannel: React.Dispatch<React.SetStateAction<ChatType | null>>;
  isChatsLoaded: boolean;
  setIsChatsLoaded: React.Dispatch<React.SetStateAction<boolean>>;
}

const ChatContext = createContext<ChatContextProps | undefined>(undefined);

const LISTING_STORAGE_KEY = 'chat_listing_info';
const ERROR_STORAGE_KEY = 'chat_error';

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const [channels, setChannels] = useState<ChatType[]>([]);
  const [writeListingInfo, setWriteListingInfo] =
    useState<WriteListingInfo | null>(null);
  const [chatError, setChatError] = useState<boolean>(false);
  const [selectedChannel, setSelectedChannel] = useState<ChatType | null>(null);
  const [isChatsLoaded, setIsChatsLoaded] = useState<boolean>(false);
  const { user } = useAuth();

  useEffect(() => {
    const stored = localStorage.getItem(ERROR_STORAGE_KEY);

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setChatError(parsed);
        localStorage.removeItem(ERROR_STORAGE_KEY);
      } catch (e) {
        console.error('Error parsing stored chat error:', e);
      }
    }

    const handleStorage = (e: StorageEvent) => {
      if (e.key === ERROR_STORAGE_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          setChatError(parsed);
          localStorage.removeItem(ERROR_STORAGE_KEY);
        } catch (err) {
          console.error('Error parsing chat_error from storage event', err);
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    const savedListingInfo = localStorage.getItem(LISTING_STORAGE_KEY);

    if (savedListingInfo) {
      try {
        setWriteListingInfo(JSON.parse(savedListingInfo));
      } catch (error) {
        console.error('Error parsing saved chat info:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('channels', JSON.stringify(channels));
  }, [channels]);

  useEffect(() => {
    setChannels(JSON.parse(localStorage.getItem('channels') || '[]'));
  }, [user]);

  const removeWriteListingInfo = () => {
    localStorage.removeItem(LISTING_STORAGE_KEY);
    setWriteListingInfo(null);
  };

  useEffect(() => {
    if (writeListingInfo) {
      localStorage.setItem(
        LISTING_STORAGE_KEY,
        JSON.stringify(writeListingInfo)
      );
    } else {
      localStorage.removeItem(LISTING_STORAGE_KEY);
    }
  }, [writeListingInfo]);

  return (
    <ChatContext.Provider
      value={{
        channels,
        setChannels,
        writeListingInfo,
        setWriteListingInfo,
        removeWriteListingInfo,
        chatError,
        setChatError,
        selectedChannel,
        setSelectedChannel,
        isChatsLoaded,
        setIsChatsLoaded
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }

  return context;
};
